import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import SelfEmployed from "../../components/modals/SelfEmployed";
import BVNVerificationModal from "../../components/modals/BVNVerificationModal";
import CreditLimitModal from "../../components/modals/CreditLimitModal";
import Employed from "../../components/modals/Employed";
import toast from "react-hot-toast";
import { useAppSelector } from "../../redux/hooks";
import { RootState } from "../../redux/store";
import Student from "../../components/modals/Student";
// import { AllBanksModal } from "../components/modals/AllBanksModal";
import { Preloader } from "../../components/elements/Preloader";
import DisapproveModal from "../../components/DisapproveModal";
import Pagination from "../../components/Pagination";
import { IoSearchOutline } from "react-icons/io5";
import { FaPhone, FaRegCopy, FaWhatsapp } from "react-icons/fa6";
import LoadingSpinner from "../../components/elements/LoadingSpinner";
import BankStatementModal from "../../components/modals/BankStatementModal";

const formatDate = (dateStr: string | undefined): string => {
  if (!dateStr || typeof dateStr !== "string") {
    return "Not specified";
  }

  try {
    const date = new Date(dateStr);
    const options: Intl.DateTimeFormatOptions = {
      year: "numeric",
      month: "long",
      day: "numeric",
    };
    const formattedDate = date.toLocaleDateString("en-US", options);

    return formattedDate;
  } catch (e) {
    console.error(`formatDate: Error formatting date "${dateStr}":`, e);
    return dateStr;
  }
};

// Helper function to display business owner status
const formatBusinessOwner = (
  isBusinessOwner: boolean | undefined | null
): string => {
  // Handle cases where the field might not exist in older user records
  if (isBusinessOwner === undefined || isBusinessOwner === null) {
    return "Not specified";
  }
  return isBusinessOwner ? "Yes" : "No";
};

export const OyoDocumentCheck = () => {
  const { token } = useAppSelector((store: RootState) => store.auth);
  const [bvnModalOpen, setBvnModalOpen] = useState(false);
  const [studentModalOpen, setStudentModalOpen] = useState(false);
  const [selfEmployedModalOpen, setSelfEmployedModalOpen] = useState(false);
  const [filter, setFilter] = useState("all");
  const [selectedBvn, setSelectedBvn] = useState<any>({});
  const [selectedInfo, setSelectedInfo] = useState<any>({});
  const [creditLimit, setCreditLimit] = useState("");
  const [action1, setAction1] = useState(false);
  const [disapproveMsg, setDisapproveMsg] = useState("");
  const [action2, setAction2] = useState("");
  const [showMsg, setShowMsg] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [monoLoading, setMonoLoading] = useState(false);
  const [virtualLoading, setVirtualLoading] = useState(false);
  const [approveLoading, setApproveLoading] = useState(false);
  const [employedModalOpen, setEmployedModalOpen] = useState(false);
  const [bankStatement, setBankStatement] = useState("");
  const [bankStatementModalOpen, setBankStatementModalOpen] = useState(false);
  const [selectedUserName, setSelectedUserName] = useState("");
  const [id, setId] = useState("");
  const [monoModal, setMonoModal] = useState(false);
  const [virtualAcctModal, setVirtualAcctModal] = useState(false);
  const [allAdmins, setAllAdmins] = useState<any>([]);
  const [searchedUser, setSearchedUser] = useState<any>([]);
  const [searchResults, setSearchResults] = useState<any>([]);
  const [govUsers, setGovUsers] = useState<any>([]);
  const [totalPages, setTotalPages] = useState(1);
  const [totalResults, setTotalResults] = useState(0);

  const [itemsPerPage] = useState(20);
  const [currentPage, setCurrentPage] = useState(1);

  // Fetch stage2 users using the new optimized endpoint
  const getAllUsers = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/getCustomersByCategoryAndVerification?categoryType=stateGovernment&adminVerification=stage2&page=${currentPage}&limit=${itemsPerPage}`,
        {
          headers: {
            Authorization: token,
          },
        }
      );

      if (res.data) {
        setGovUsers(res.data.users || []);
        setTotalPages(res.data.totalPages || 1);
        setTotalResults(res.data.totalResults || 0);
      }
    } catch (error: any) {
      console.error("Error fetching state government users:", error);
      toast.error(error?.response?.data?.message || "Failed to load users");
    } finally {
      setIsLoading(false);
    }
  }, [token, currentPage, itemsPerPage]);

  // Fetch users when component mounts or page changes
  useEffect(() => {
    getAllUsers();
  }, [getAllUsers]);

  // Pagination handlers
  const handlePagination = (pageNumber: number) => {
    setCurrentPage(pageNumber);
  };

  const prevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const nextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  // Memoize getAllAdmins to prevent recreation on every render
  const getAllAdmins = useCallback(async () => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/get-admins`,
        {
          headers: {
            Authorization: token,
          },
        }
      );
      setAllAdmins(res?.data);
    } catch (error) {
      console.error(error);
    }
  }, [token]);

  // Fetch admins only once on mount
  useEffect(() => {
    getAllAdmins();
  }, [getAllAdmins]);

  const approveCustomerHandler = async () => {
    const checkCreditLimit: any = govUsers?.filter(
      (user: any) => user._id === id
    );
    if (!checkCreditLimit[0].isWallet) {
      toast.error("Set credit limit first!");
      return;
    }
    setApproveLoading(true);
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_API_URL}/approveStageTwoProcess/${id}`,
        {},
        {
          headers: {
            Authorization: token,
          },
        }
      );

      if (res?.status === 200) {
        setId("");
        setAction2("");
        toast.success(res?.data?.message);
        getAllUsers();
        await getAllAdmins();
      }
    } catch (error) {
      console.error(error);
      setAction2("");
      toast.error("Error approving user, please try again later");
    } finally {
      setApproveLoading(false);
    }
  };

  const createVirtualAcct = async (id: string) => {
    setVirtualLoading(true);
    try {
      await axios.post(
        `${process.env.REACT_APP_API_URL}/create-virtual-account/${id}`,
        {},
        {
          headers: {
            Authorization: token,
          },
        }
      );
      getAllUsers();
      setVirtualAcctModal(false);
      toast.success("Virtual account created");
    } catch (error) {
      console.error(error);
    } finally {
      setVirtualLoading(false);
      setVirtualAcctModal(false);
    }
  };

  const createMonoAcct = async (id: string) => {
    setMonoLoading(true);
    try {
      await axios.put(
        `${process.env.REACT_APP_API_URL}/create-mono-account/${id}`,
        {},
        {
          headers: {
            Authorization: token,
          },
        }
      );
      getAllUsers();
      setMonoModal(false);
      toast.success("Mono account created");
    } catch (error: any) {
      console.error(error);
      toast.error(
        error.message || "Error creating Mono account, please try again later"
      );
    } finally {
      setMonoLoading(false);
      setMonoModal(false);
    }
  };

  const handleAction = (e: any) => {
    setAction2(e.target.value);
  };

  const handleClearSelection = (e: any) => {
    setAction2("");

    const selectElement = document.getElementById("select") as any;
    if (selectElement) {
      selectElement.selectedIndex = 0;
    }
  };

  const handleBvnClick = (bvn: string) => {
    setSelectedBvn(bvn);
    setBvnModalOpen(true);
  };

  // This function is kept for future use when occupation details need to be displayed
  // Currently not used since all users are "Civil Servant" but maintained for consistency with other document check pages
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleJobClick = (
    details: string,
    occupation: any,
    bankStatement: string
  ) => {
    setSelectedInfo(details);
    setBankStatement(bankStatement);
    if (occupation === "Self-Employed") {
      setSelfEmployedModalOpen(true);
    } else if (occupation === "Employed") {
      setEmployedModalOpen(true);
    } else {
      setStudentModalOpen(true);
    }
  };

  const handleDisapproveClick = (msg: string) => {
    setDisapproveMsg(msg);
    setShowMsg(true);
  };

  const handleBankStatementClick = (
    bankStatementUrl: string,
    userName: string
  ) => {
    setBankStatement(bankStatementUrl);
    setSelectedUserName(userName);
    setBankStatementModalOpen(true);
  };

  const handleConfirmation = async (e: any, userId: string) => {
    const payload = {
      documentCheck: true,
    };

    try {
      const res = await axios.patch(
        `${process.env.REACT_APP_API_URL}/update-document-check/${userId}`,
        payload,
        {
          headers: {
            Authorization: token,
          },
        }
      );
      getAllUsers();
      toast.success(res?.data?.message || "Account confirmed!");
    } catch (error: any) {
      console.error(error);
      toast.error(error.message || "Confirmation failed");
    } finally {
      setIsLoading(false);
    }
  };

  // Memoize search function to prevent recreation on every render
  const searchForUsersWithEmail = useCallback(
    (value: string) => {
      setSearchedUser(value);
      if (value.length > 0) {
        const searchResult =
          govUsers?.filter((user: any) =>
            user.email?.toLowerCase().includes(value.toLowerCase())
          ) || null;
        setSearchResults(searchResult);
      } else {
        setSearchResults([]);
      }
    },
    [govUsers]
  );

  const handleCopyClick = async (text: any) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Copied to clipboard!");
    } catch (err) {
      console.error("Failed to copy: ", err);
    }
  };

  // Memoize data to prevent recalculation on every render
  const data = useMemo(
    () => (searchedUser && searchedUser?.length > 0 ? searchResults : govUsers),
    [searchedUser, searchResults, govUsers]
  );

  return (
    <main className="overflow-x-auto w-full">
      <div className="bg-white rounded-md shadow-md pb-6">
        <div className={`w-full overflow-x-auto`}>
          <div
            className="flex items-center justify-between p-6"
            style={{ minWidth: "700px" }}
          >
            <h1 className="text-base font-semibold ">Oyo Document Check</h1>
            <div className="relative md:w-[30rem] w-fit">
              <IoSearchOutline className="w-6 h-6 absolute top-[0.6rem] left-2 text-gray-300" />
              <input
                type="search"
                name="searchedUser"
                id="searchedUser"
                value={searchedUser}
                onChange={(e) => searchForUsersWithEmail(e.target.value)}
                placeholder="Search user using email"
                className="border p-2 rounded-md indent-7 w-full"
                disabled={govUsers?.length === 0}
              />
            </div>

            <label htmlFor="filterOccupation">
              Sort by:
              <select
                name="filterOccupation"
                id="filterOccupation"
                className="border p-2 ml-4"
                onChange={(e) => setFilter(e.target.value)}
              >
                <option value="all">All</option>
                <option value="employed">Employed</option>
                <option value="self-employed">Self-employed</option>
                <option value="student">Students</option>
              </select>
            </label>
          </div>
          <section className="overflow-y-auto">
            <table className="w-full" style={{ minWidth: "1200px" }}>
              <thead className="bg-gray-50 text-left">
                <tr>
                  <th className="p-2">S/N</th>
                  <th className="p-2">Name</th>
                  <th className="p-2">Email</th>
                  <th className="p-2 text-nowrap">Phone Number</th>
                  <th className="p-2 text-nowrap">Contact</th>
                  <th className="p-2">BVN</th>
                  <th className="p-2">Occupation</th>
                  <th className="p-2 text-nowrap">Date In</th>
                  <th className="p-2">Admin Officer</th>
                  <th className="p-2 text-nowrap">Business Owner</th>
                  <th className="p-2 text-nowrap">Action 1</th>
                  <th className="p-2 text-nowrap">Action 2 MA</th>
                  <th className="p-2 text-nowrap">Action 3 VA</th>
                  <th className="p-2 text-nowrap">Action 4</th>
                  <th className="p-2">Confirmation</th>
                  <th className="p-2">Bank Statement</th>
                  <th className="p-2">Status</th>
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  <tr className="p-10 text-center">
                    <td colSpan={14}>
                      <LoadingSpinner />
                    </td>
                  </tr>
                ) : data && data?.length ? (
                  data
                    .filter((user: any) => {
                      if (filter === "employed") {
                        return user.employmentStatus === "employed";
                      } else if (filter === "self-employed") {
                        return user.employmentStatus === "selfEmployed";
                      } else if (filter === "student") {
                        return user.employmentStatus === "student";
                      } else if (filter === "all") {
                        return user.employmentStatus;
                      }
                      return true;
                    })
                    .slice()
                    .reverse()
                    .map((user: any, index: number) => {
                      return (
                        <tr className="border-b" key={index}>
                          <td className="text-secondary p-2">
                            {(currentPage - 1) * itemsPerPage + index + 1}
                          </td>
                          <td className="p-2 text-nowrap">
                            {user.lastName} {user.firstName}
                          </td>
                          <td className="p-2">
                            <div className="flex items-center gap-2">
                              <button
                                type="button"
                                onClick={() => handleCopyClick(user.email)}
                              >
                                <FaRegCopy className="w-5 h-5 text-gray-500" />
                              </button>
                              <p>{user.email}</p>
                            </div>
                          </td>

                          <td className="p-2">{user.phoneNumber}</td>
                          <td className="p-2 ">
                            <button
                              type="button"
                              className="mx-2"
                              title="call customer"
                            >
                              <a href={`tel:${user?.phoneNumber}`}>
                                <FaPhone />
                              </a>
                            </button>
                            <button type="button" title="text via whatsapp">
                              <a
                                href={`https://wa.me/${user?.phoneNumber?.replace(
                                  /^0/,
                                  "234"
                                )}`}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                <FaWhatsapp />
                              </a>
                            </button>
                          </td>

                          <td className="p-2">
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => handleBvnClick(user)}
                                className="text-blue-500 hover:underline focus:outline-none"
                              >
                                {user.bvn}
                              </button>
                              <button
                                type="button"
                                className=""
                                onClick={() => handleCopyClick(user.bvn)}
                              >
                                <FaRegCopy className="w-5 h-5 text-gray-500" />
                              </button>
                            </div>
                          </td>
                          <td className="p-2">Civil Servant</td>
                          <td className="p-2 text-nowrap">
                            {formatDate(user.createdAt)}
                          </td>
                          <td className="p-2 text-nowrap">
                            {(() => {
                              const admin = allAdmins?.find(
                                (item: any) => item?.id === user.adminOfficer
                              );

                              return (
                                <span>
                                  {admin
                                    ? `${admin.lastName} ${admin.firstName}`
                                    : "-- --"}
                                </span>
                              );
                            })()}
                          </td>
                          <td className="p-2 text-nowrap">
                            <span
                              className={`px-2 py-1 rounded-full text-xs font-medium ${
                                formatBusinessOwner(user.isBusinessOwner) ===
                                "Yes"
                                  ? "bg-green-100 text-green-800"
                                  : formatBusinessOwner(
                                      user.isBusinessOwner
                                    ) === "No"
                                  ? "bg-red-100 text-red-800"
                                  : "bg-gray-100 text-gray-800"
                              }`}
                            >
                              {formatBusinessOwner(user.isBusinessOwner)}
                            </span>
                          </td>
                          <td className="p-2 text-nowrap text-secondary">
                            {user.isWallet ? (
                              "Done"
                            ) : (
                              <button
                                onClick={() => {
                                  setAction1((prev) => !prev);
                                  setId(user._id);
                                }}
                                className="text-blue-500 hover:underline focus:outline-none"
                              >
                                Set Credit Limit
                              </button>
                            )}
                          </td>
                          <td className="p-2 text-center">
                            <button
                              className={`p-2 border text-sm rounded-md ${
                                !user.isWallet
                                  ? "bg-gray-300 text-black"
                                  : "bg-secondary text-white"
                              }`}
                              onClick={() => {
                                setId(user._id);
                                setMonoModal((prev) => !prev);
                              }}
                              disabled={
                                (!user.isWallet && !user.creditLimit) ||
                                Object.keys(user.monoAccount).length !== 0
                              }
                              title={
                                !user.isWallet && !user.creditLimit
                                  ? "set credit limit first"
                                  : ""
                              }
                            >
                              {Object.keys(user.monoAccount).length !== 0
                                ? "Done"
                                : "Create"}
                            </button>
                          </td>
                          <td className="p-2 text-center">
                            <button
                              className={`p-2 border text-sm rounded-md ${
                                Object.keys(user.monoAccount).length === 0
                                  ? "bg-gray-300 text-black"
                                  : "bg-secondary text-white"
                              }`}
                              onClick={() => {
                                setId(user._id);
                                setVirtualAcctModal((prev) => !prev);
                              }}
                              disabled={
                                Object.keys(user.monoAccount).length === 0 ||
                                user.isPaystackIdentified
                              }
                              title={
                                Object.keys(user.monoAccount).length === 0
                                  ? "create mono account first"
                                  : ""
                              }
                            >
                              {user.isPaystackIdentified ? "Done" : "Create"}
                            </button>
                          </td>
                          <td className="p-2">
                            <select
                              name="select"
                              id="select"
                              className="cursor-pointer"
                              onChange={(e) => {
                                handleAction(e);
                                setId(user._id);
                              }}
                              // if VA isn't created, disable this select
                              // disabled={!user.virtualAccount.accountName}
                              // if VA is creation is 200, but pending, disable this select
                              // disabled={!user.isPaystackIdentified}

                              // if mono isn't created, disable this select
                              disabled={
                                Object.keys(user.monoAccount).length === 0
                              }
                              title={
                                !user.isPaystackIdentified
                                  ? "create virtual account first"
                                  : ""
                              }
                            >
                              <option value="">Select an action</option>
                              <option value="approve">Approve</option>
                              <option value="disapprove">Disapprove</option>
                            </select>
                          </td>

                          <td className="p-2 text-center">
                            <input
                              type="checkbox"
                              name="confirmation"
                              id="confirmation"
                              checked={user.documentCheck}
                              disabled={user.documentCheck}
                              onChange={(e) => {
                                handleConfirmation(e, user._id);
                              }}
                            />
                          </td>
                          <td className="p-2 text-center">
                            <button
                              onClick={() =>
                                handleBankStatementClick(
                                  user.bankStatement,
                                  `${user.lastName} ${user.firstName}`
                                )
                              }
                              className="p-2 border text-sm bg-secondary text-white rounded-md hover:bg-opacity-90 transition-colors cursor-pointer"
                            >
                              View
                            </button>
                          </td>
                          <td className="p-2">
                            <button
                              onClick={
                                user.msg
                                  ? () => handleDisapproveClick(user.msg)
                                  : undefined
                              }
                              className={
                                user.adminVerification === "dstage2"
                                  ? "text-red-600"
                                  : "text-yellow-400"
                              }
                            >
                              {user.adminVerification === "dstage2"
                                ? "Disapproved"
                                : "Pending"}
                            </button>
                          </td>
                        </tr>
                      );
                    })
                ) : (
                  <tr>
                    <td colSpan={16} className="text-center p-3 text-secondary">
                      {searchedUser?.length > 0
                        ? "User not found"
                        : "Not found"}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </section>
        </div>
        <section className="p-3 my-5">
          <Pagination
            length={
              searchedUser?.length > 0 ? searchResults.length : totalResults
            }
            itemsPerPage={itemsPerPage}
            handlePagination={handlePagination}
            currentPage={currentPage}
            prevPage={prevPage}
            nextPage={nextPage}
          />
        </section>
      </div>
      {showMsg && (
        <div className="fixed inset-0 flex items-center justify-center bg-gray-900 bg-opacity-50">
          <div className="bg-white p-4 rounded-md" style={{ width: "500px" }}>
            <div className="flex justify-between flex-col gap-5 items-center  text-dark p-2 rounded-t-md mb-4">
              <div className="relative w-full">
                <button
                  onClick={() => setShowMsg(false)}
                  className="text-dark absolute right-0"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-6 w-6"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>

              <p>{disapproveMsg}</p>
              <div>
                <button
                  type="button"
                  onClick={() => setShowMsg(false)}
                  className="w-20 px-5 py-3 mr-3 rounded text-white bg-[#008b50]"
                >
                  Ok
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {action2 === "approve" && (
        <div className="fixed z-40 inset-0 flex items-center justify-center bg-gray-900 bg-opacity-50">
          <div className="bg-white p-4 rounded-md" style={{ width: "500px" }}>
            <div className="flex justify-between flex-col gap-5 items-center  text-dark p-2 rounded-t-md mb-4">
              <div className="relative w-full">
                <button
                  onClick={handleClearSelection}
                  className="text-dark absolute right-0"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-6 w-6"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>

              <p>Are you sure you want approve this user?</p>
              <div>
                <button
                  type="button"
                  className="w-20 px-5 py-3 mr-3 rounded text-white bg-[#008b50]"
                  onClick={approveCustomerHandler}
                >
                  {approveLoading ? <Preloader /> : "Yes"}
                </button>
                <button
                  type="button"
                  className="w-20 px-5 py-3 bg-red-500 rounded text-white "
                  onClick={handleClearSelection}
                >
                  No
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {monoModal && (
        <div className="fixed z-40 inset-0 flex items-center justify-center bg-gray-900 bg-opacity-50">
          <div className="bg-white p-4 rounded-md" style={{ width: "500px" }}>
            <div className="flex justify-between flex-col gap-5 items-center  text-dark p-2 rounded-t-md mb-4">
              <div className="relative w-full">
                <button
                  onClick={() => setMonoModal(false)}
                  className="text-dark absolute right-0"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-6 w-6"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>

              <p className="text-center">
                Are you sure you want create a Mono Account for this user?
              </p>
              <div>
                <button
                  type="button"
                  className="w-20 px-5 py-3 mr-3 rounded text-white bg-[#008b50]"
                  onClick={() => createMonoAcct(id)}
                >
                  {monoLoading ? <Preloader /> : "Yes"}
                </button>
                <button
                  type="button"
                  className="w-20 px-5 py-3 bg-red-500 rounded text-white "
                  onClick={() => setMonoModal(false)}
                >
                  No
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {virtualAcctModal && (
        <div className="fixed z-40 inset-0 flex items-center justify-center bg-gray-900 bg-opacity-50">
          <div className="bg-white p-4 rounded-md" style={{ width: "500px" }}>
            <div className="flex justify-between flex-col gap-5 items-center  text-dark p-2 rounded-t-md mb-4">
              <div className="relative w-full">
                <button
                  onClick={() => setVirtualAcctModal(false)}
                  className="text-dark absolute right-0"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-6 w-6"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>

              <p className="text-center">
                Are you sure you want to create a Virtual Account for this user?
              </p>
              <div>
                <button
                  type="button"
                  className="w-20 px-5 py-3 mr-3 rounded text-white bg-[#008b50]"
                  onClick={() => createVirtualAcct(id)}
                >
                  {virtualLoading ? <Preloader /> : "Yes"}
                </button>
                <button
                  type="button"
                  className="w-20 px-5 py-3 bg-red-500 rounded text-white "
                  onClick={() => setVirtualAcctModal(false)}
                >
                  No
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {action2 === "disapprove" && (
        <DisapproveModal
          handleClearSelection={handleClearSelection}
          id={id}
          setId={setId}
          setActionSelected={setAction2}
          stage={"documentCheck"}
          getAllUsers={getAllUsers}
        />
      )}

      {action1 && (
        <CreditLimitModal
          id={id}
          setId={setId}
          setAction1={setAction1}
          creditLimit={creditLimit}
          setCreditLimit={setCreditLimit}
          getAllUsers={getAllUsers}
          getAllAdmins={getAllAdmins}
        />
      )}
      {bvnModalOpen && (
        <BVNVerificationModal
          setBvnModalOpen={setBvnModalOpen}
          selectedBvn={selectedBvn}
        />
      )}

      {/* {allBanksModal && (
        <AllBanksModal
          setAllBanksModal={setAllBanksModal}
          setAllBanksDetails={setAllBanksDetails}
          allBanksDetails={allBanksDetails}
          userId={userId}
          getAllUsers={getAllUsers}
          documentCheckPage={documentCheckPage}
          setDocumentCheckPage={setDocumentCheckPage}
        />
      )} */}

      {studentModalOpen && (
        <Student
          bankStatement={bankStatement}
          selectedInfo={selectedInfo}
          setStudentModalOpen={setStudentModalOpen}
        />
      )}
      {selfEmployedModalOpen && (
        <SelfEmployed
          bankStatement={bankStatement}
          setSelfEmployedModalOpen={setSelfEmployedModalOpen}
          selectedInfo={selectedInfo}
        />
      )}
      {employedModalOpen && (
        <Employed
          bankStatement={bankStatement}
          setEmployedModalOpen={setEmployedModalOpen}
          selectedInfo={selectedInfo}
        />
      )}
      {bankStatementModalOpen && (
        <BankStatementModal
          bankStatementUrl={bankStatement}
          userName={selectedUserName}
          onClose={() => setBankStatementModalOpen(false)}
        />
      )}
    </main>
  );
};
